import React from "react";

import { TimeSeriesUnits } from "~/modules/token_timeseries/types";
import { formatApy } from "~/utils/functions/formatApy";
import { formatCurrencyValueCompact } from "~/utils/functions/formatCurrencyValueCompact";

// // // //

/** Must stay in sync with `tooltip.width` on `AssetScreenerListingDualAxisChart`. */
export const LISTING_DUAL_AXIS_TOOLTIP_WIDTH_PX = 120;

export interface ListingDualAxisTooltipRow {
    label: string;
    color: string | null;
    value: number | null;
    unit: TimeSeriesUnits;
}

interface Props {
    dateLabel: string;
    rows: ListingDualAxisTooltipRow[];
}

function formatListingTooltipValue(row: ListingDualAxisTooltipRow): string {
    const { value, unit } = row;
    if (value === null || Number.isNaN(value)) {
        return "—";
    }

    if (unit === TimeSeriesUnits.dollar || unit === TimeSeriesUnits.dollars) {
        return `$${formatCurrencyValueCompact(value)}`;
    }

    if (unit === TimeSeriesUnits.percent) {
        return `${formatApy(value)}%`;
    }

    return value.toLocaleString();
}

/**
 * Compact tooltip for dual-axis listing charts (AUM + APY): outer width matches chart `tooltip.width`;
 * inner grid uses `auto` + `1fr` so values fill remaining space.
 */
export function ListingDualAxisTooltip(props: Props) {
    const { dateLabel, rows } = props;
    const rowCount = Math.max(rows.length, 1);

    const tooltipDateClass =
        "text-[12px] font-normal leading-snug text-slate-800";
    const tooltipValueClass =
        "text-[12px] font-semibold leading-snug text-slate-800";

    return (
        <div
            className="font-sans box-border grid shrink-0 grid-cols-[auto_minmax(0,1fr)] select-none gap-x-2 gap-y-0 rounded-md bg-slate-200 px-1 py-1 text-slate-800"
            style={{
                width: LISTING_DUAL_AXIS_TOOLTIP_WIDTH_PX,
                minWidth: LISTING_DUAL_AXIS_TOOLTIP_WIDTH_PX,
                maxWidth: LISTING_DUAL_AXIS_TOOLTIP_WIDTH_PX,
                backgroundColor: "#e2e8f0",
            }}
        >
            <p
                className={`min-w-0 wrap-break-word text-left ${tooltipDateClass}`}
                style={{ gridColumn: 1, gridRow: `1 / span ${rowCount}` }}
            >
                {dateLabel}
            </p>
            {rows.map((row, i) => (
                <span
                    key={`${row.label}-${String(i)}`}
                    className={`block min-w-0 truncate text-right tabular-nums ${tooltipValueClass}`}
                    style={{ gridColumn: 2, gridRow: i + 1 }}
                >
                    {formatListingTooltipValue(row)}
                </span>
            ))}
        </div>
    );
}
